import React, { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../layout-components/Breadcrumbs";
import DataTable, { Column } from "../../layout-components/DataTable";
import FeatherIcon from "feather-icons-react";
import { useNavigate } from "react-router-dom";
import { _post } from "../../services/apiClient";
import { apiUrls } from "../../config/apiUrls.const";
import { useBusiness } from "../../contexts/BusinessContext";
import { Form, Row, Col, Button } from "react-bootstrap";
import { OrderViewModal, OrderViewModalRef } from "./OrderViewModal";

export default function OrderList() {
  const navigate = useNavigate();
  const { business } = useBusiness();
  const orderModalRef = React.useRef<OrderViewModalRef>(null);

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [statusFilter, setStatusFilter] = useState(0); // 0: All
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination States matching Laravel response metadata structures
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);

  const fetchOrders = useCallback(async (pageNumber = 1) => {
    if (!business?.id) return;

    try {
      setLoading(true);
      const reqBody = {
        page: pageNumber,
        per_page: perPage,
        business_id: business.id,
        status: statusFilter,
        search: searchTerm,
      };

      // Ensure apiUrls.getOrderList exists in your constants matching this route payload configuration
      const response = await _post(apiUrls.getOrderList || "/orders/list", reqBody);

      // 1. Unpack root JSON payload sequence
      const rootJson = response.data?.success !== undefined ? response.data : response;

      // 2. Extract nested data block collection
      const paginationData = rootJson?.data;

      // 3. Extract your array mapping structure safely
      // If the backend returns it directly as an unpaginated array fallback, use rootJson.data
      const orderArray = Array.isArray(paginationData) ? paginationData : (paginationData?.data || []);

      setData(orderArray);
      setCurrentPage(paginationData?.current_page || 1);
      setLastPage(paginationData?.last_page || 1);
      setPerPage(paginationData?.per_page || 20);
      setTotalItems(paginationData?.total || orderArray.length);

    } catch (err) {
      console.error("Failed to fetch orders array from API context", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [business?.id, statusFilter, searchTerm, perPage]);

  // Handle page changes gracefully
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= lastPage) {
      fetchOrders(page);
    }
  };

  // Re-run whenever business details or configuration filters toggle
  useEffect(() => {
    fetchOrders(1);
  }, [business?.id, statusFilter]);

  // Columns definition matching your target nested data payload object paths precisely
  const columns: Column<any>[] = [
    { 
      key: "id", 
      label: "SL",
      render: (item: any, index?: number) => (
        <span className="fw-medium text-muted">
          {((currentPage - 1) * perPage) + (index ?? 0) + 1}
        </span>
      )
    },
    { 
      key: "booking_id", 
      label: "Booking Ref ID",
      render: (item: any) => (
        <span className="fw-medium text-dark font-monospace">
          BK-{item.booking_id || "N/A"}
        </span>
      )
    },
    {
      key: "customer",
      label: "Customer Details",
      render: (item: any) => (
        <div>
          <div className="fw-bold text-dark">{item.customer?.name || "Guest Checkout"}</div>
          <small className="text-muted">{item.customer?.mobile_no || "No Contact"}</small>
        </div>
      )
    },
    {
      key: "order_items",
      label: "Items Purchased",
      render: (item: any) => {
        const itemsCount = item.order_items?.length || 0;
        return (
          <span className="small fw-medium text-secondary">
            {itemsCount > 0 ? `${itemsCount} item(s) tracked` : "0 items (Empty Order)"}
          </span>
        );
      }
    },
    {
      key: "grand_total",
      label: "Total Amount",
      render: (item: any) => (
        <span className="fw-bold text-primary">
          {item.pricing?.grand_total ? `${parseFloat(item.pricing.grand_total).toFixed(2)} BDT` : "0.00 BDT"}
        </span>
      )
    },
    {
      key: "payment_status",
      label: "Payment Info",
      render: (item: any) => {
        const payStatus = item.statuses?.payment_status;
        const method = item.statuses?.payment_method?.toUpperCase() || "COD";
        return (
          <div>
            <span className={`badge ${payStatus === 1 ? 'bg-success' : 'bg-warning'} text-uppercase small px-2 py-1`}>
              {payStatus === 1 ? 'Paid' : 'Unpaid'}
            </span>
            <div className="text-muted mt-1" style={{ fontSize: '11px' }}>Method: {method}</div>
          </div>
        );
      }
    },
    {
      key: "order_status",
      label: "Order Status",
      render: (item: any) => {
        const orderStatus = item.statuses?.order_status;
        let statusLabel = "Pending";
        let badgeColor = "bg-secondary";

        switch (orderStatus) {
          case 0: statusLabel = "Pending"; badgeColor = "bg-warning"; break;
          case 1: statusLabel = "Confirmed"; badgeColor = "bg-info"; break;
          case 2: statusLabel = "Completed"; badgeColor = "bg-success"; break;
          case 3: statusLabel = "Cancelled"; badgeColor = "bg-danger"; break;
        }

        return (
          <span className={`badge ${badgeColor} text-uppercase small px-2 py-1`}>
            {statusLabel}
          </span>
        );
      }
    },
    {
      key: "created_at",
      label: "Created Timeline",
      render: (item: any) => {
        if (!item.created_at) return <span className="text-muted">N/A</span>;
        const date = new Date(item.created_at);
        return (
          <div>
            <div className="text-dark small fw-medium">
              {date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
            <small className="text-muted">
              {date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </small>
          </div>
        );
      }
    },
  ];

  return (
    <div className="container-fluid p-0">
      <h1 className="h3 mb-3">Order List</h1>

      <div className="row">
        <div className="col-12">
          {/* --- SEARCH & FILTER SECTION --- */}
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-body">
          <Row className="align-items-end g-3">
            <Col md={5}>
              <Form.Label className="fw-bold text-secondary">Search Order Queries</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter customer details..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchOrders(1)}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="fw-bold text-secondary">Operational Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(Number(e.target.value))}
              >
                <option value={0}>All Orders Status</option>
                <option value={1}>Pending Verification</option>
                <option value={2}>Confirmed Processing</option>
                <option value={3}>Completed Shipments</option>
                <option value={4}>Cancelled Requests</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Button className="btn btn-outline-info" onClick={() => fetchOrders(1)}>
                <FeatherIcon icon="search" size={15} className="me-2" /> Search
              </Button>
            </Col>
          </Row>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
          <h5 className="card-title mb-0 fw-bold text-dark">Order Registry Data</h5>
          <button className="btn btn-primary btn-md fw-medium px-3" onClick={() => navigate("/orders/new")}>
            <FeatherIcon icon="plus" size={15} className="me-1" /> New Order
          </button>
        </div>
        
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary spinner-border-sm"></div>
            </div>
          ) : (
            <>
              {/* --- DYNAMIC DATATABLE INGESTION CONTAINER --- */}
              <DataTable
  data={data}
  columns={columns}
  onView={(item) => orderModalRef.current?.open(item)} // <-- FIXED 
  onEdit={(item) => navigate(`/order/edit/${item.id}`)}
  onDelete={(item) => console.log("Delete Request Captured For Order ID:", item.id)}
/>

              {/* --- PAGINATION FOOTER CONTROL --- */}
              {lastPage > 1 && (
                <div className="card-footer bg-white d-flex justify-content-between align-items-center px-4 py-3 border-top">
                  <div className="small text-muted">
                    Showing entries {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} entries
                  </div>

                  <nav aria-label="Order Registry Pagination navigation">
                    <ul className="pagination d-flex list-unstyled align-items-center mb-0 gap-1">
                      {/* Previous Button */}
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link rounded px-3 py-1"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                        >
                          &laquo;
                        </button>
                      </li>

                      {/* Dynamic Page Numbers Block Builder Loop */}
                      {(() => {
                        let items = [];
                        for (let number = 1; number <= lastPage; number++) {
                          if (number === 1 || number === lastPage || Math.abs(currentPage - number) <= 1) {
                            items.push(
                              <li key={number} className={`page-item ${number === currentPage ? 'active' : ''}`}>
                                <button
                                  className={`page-link rounded px-3 py-1 ${number === currentPage ? 'bg-primary text-white' : ''}`}
                                  onClick={() => handlePageChange(number)}
                                >
                                  {number}
                                </button>
                              </li>
                            );
                          } else if (number === 2 || number === lastPage - 1) {
                            items.push(
                              <li key={`ellipsis-${number}`} className="page-item disabled px-2">
                                <span className="text-muted">&dots;</span>
                              </li>
                            );
                          }
                        }
                        return items;
                      })()}

                      {/* Next Button */}
                      <li className={`page-item ${currentPage === lastPage ? 'disabled' : ''}`}>
                        <button
                          className="page-link rounded px-3 py-1"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === lastPage}
                          style={{ cursor: currentPage === lastPage ? 'not-allowed' : 'pointer' }}
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </div>
        </div>
      </div>
      {/* Reusable Core Modal Target */}
      <OrderViewModal ref={orderModalRef} />
    </div>
  );
}